import ConfirmModal from "@/src/components/action-modals/confirm-modal";
import { SelectableItem } from "@/src/components/action-modals/item-selection/item-row";
import Step1Details from "@/src/components/events/event-form/step-one-details";
import Step3Review from "@/src/components/events/event-form/step-three-review";
import Step2Settings from "@/src/components/events/event-form/step-two-settings";
import ScStepper from "@/src/components/ui-kit/stepper";
import { useUploadFile } from "@/src/hooks/features/use-upload-file";
import { Category } from "@/src/models/category.model";
import {
  EventItem,
  EventType,
  eventRegistrationPolicy,
  InsertEventInput,
  Step1FormValues,
  Step2FormValues,
  visibility,
} from "@/src/models/event.model";
import { useGetCategoriesQuery } from "@/src/services/api/category/category.hooks";
import {
  useCreateEventMutation,
  useUpdateEventMutation,
} from "@/src/services/api/events/events.hooks";
import { useTheme } from "@/src/styles/theme-provider";
import { format, parseISO } from "date-fns";
import { useRouter } from "expo-router";
import { ArrowLeft } from "iconsax-react-nativejs";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ScrollView, Text, TouchableOpacity, View } from "react-native";
import Toast from "react-native-toast-message";

const EVENT_STEPS = [
  { title: "LABEL_DETAILS", description: "HINT_DETAILS_DESCRIPTION" },
  { title: "LABEL_SETTINGS", description: "HINT_SETTINGS_DESCRIPTION" },
  { title: "LABEL_REVIEW", description: "HINT_REVIEW_DESCRIPTION" },
];

const mapEventToStep1 = (event: EventItem): Step1FormValues => {
  const start = parseISO(event.startTime);
  const end = parseISO(event.endTime);

  return {
    title: event.title,
    description: event.description ?? "",
    singleDay: event.singleDay ?? true,
    startDate: format(start, "yyyy-MM-dd"),
    endDate: format(end, "yyyy-MM-dd"),
    startTime: event.startTime,
    endTime: event.endTime,
    locationName: event.locationName ?? "",
    locationLat: event.locationLat ?? 0,
    locationLng: event.locationLng ?? 0,
    categoryIds: event.categoryIds ?? [],
    coverImage: null,
    attachments: [],
    existingAttachments: event.attachmentUrls ?? [],
    // CR-B2-10: schema-blocked; defaults to InPerson for legacy events (open item 5)
    eventType: event.eventType ?? EventType.IN_PERSON,
    meetingLink: event.meetingLink ?? "",
  };
};

// CR-B2-07: Timezone decision — store as UTC ISO; display in viewer's local timezone.
// Pre-existing events (bare strings without TZ) are interpreted as local time by JS Date
// constructor, so they display without shifting hours or "Invalid Date".
const toUtcIso = (date: string, time: string): string => {
  if (!date && !time) return "";
  if (!time) return new Date(date).toISOString();
  // Already a full ISO string (editing existing event from server)
  if (time.includes("T") || time.includes("Z")) {
    const d = new Date(time);
    return isNaN(d.getTime()) ? time : d.toISOString();
  }
  // HH:mm from the DateTimePicker — combine with date into local datetime, then UTC
  return new Date(`${date}T${time}:00`).toISOString();
};

const mapEventToStep2 = (event: EventItem): Step2FormValues => ({
  visibility: (event.visibility as visibility) ?? "Public",
  registrationPolicy: event.registrationPolicy ?? "None",
  capacity: event.capacity,
  groupIds: event.groupIds ?? [],
});

type EventsCreationScreenProps = Readonly<{
  eventToEdit?: EventItem;
}>;

const EventsCreationScreen = ({ eventToEdit }: EventsCreationScreenProps) => {
  const { t } = useTranslation();
  const { themeVars } = useTheme();
  const router = useRouter();

  const isEditMode = !!eventToEdit;

  const [step, setStep] = useState(1);
  const [existingCoverRemoved, setExistingCoverRemoved] = useState(false);

  useEffect(() => {
    if (eventToEdit) {
      setPage1Data(mapEventToStep1(eventToEdit));
      setPage2Data(mapEventToStep2(eventToEdit));
    }
  }, [eventToEdit]);

  const [page1Data, setPage1Data] = useState<Step1FormValues | null>(
    eventToEdit ? mapEventToStep1(eventToEdit) : null,
  );
  const [page2Data, setPage2Data] = useState<Step2FormValues | null>(
    eventToEdit ? mapEventToStep2(eventToEdit) : null,
  );
  const [cancelModalVisible, setCancelModalVisible] = useState(false);

  const { data: categoriesData } = useGetCategoriesQuery({
    tag: "events",
    pageSize: 100,
  });


  const categories: SelectableItem[] = useMemo(
    () =>
      categoriesData?.items.map((cat: Category) => ({
        itemId: cat.ItemId ?? "",
        displayName: cat.categoryName,
      })) ?? [],
    [categoriesData],
  );

  const { mutate: insertEvent, isPending: isCreating } =
    useCreateEventMutation();

  const { mutate: updateEvent, isPending: isUpdating } =
    useUpdateEventMutation();

  const { mutateAsync: uploadImage, isPending: isUploading } = useUploadFile();

  const isSubmitting = isCreating || isUpdating || isUploading;

  const goNext = () => setStep((prev) => prev + 1);
  const goBack = () => setStep((prev) => Math.max(1, prev - 1));

  const handleCancel = () => setCancelModalVisible(true);
  const handleCancelConfirm = () => {
    setCancelModalVisible(false);
    router.push("/events");
  };

  const handlePage1Submit = (data: Step1FormValues) => {
    setPage1Data(data);
    goNext();
  };

  const handlePage2Submit = (data: Step2FormValues) => {
    setPage2Data(data);
    goNext();
  };

  const handleFinalSubmit = useCallback(async () => {
    try {
      let coverImageUrl: string | undefined;

      if (page1Data?.coverImage) {
        coverImageUrl = await uploadImage({
          uri: page1Data.coverImage.uri,
          fileName: page1Data.coverImage.fileName ?? "",
          mimeType: page1Data.coverImage.mimeType,
        });
      } else if (
        isEditMode &&
        eventToEdit?.coverImage &&
        !existingCoverRemoved
      ) {
        coverImageUrl = eventToEdit.coverImage;
      }

      const newAttachmentUrls: string[] = [];

      for (const file of page1Data?.attachments ?? []) {
        const url = await uploadImage({
          uri: file.uri,
          fileName: file.name,
          mimeType: file.mimeType ?? "application/octet-stream",
        });

        if (url) newAttachmentUrls.push(url);
      }
      const isOnline = page1Data?.eventType === EventType.ONLINE;
      const finalData: InsertEventInput = {
        title: page1Data?.title ?? "",
        description: page1Data?.description ?? "",
        singleDay: page1Data?.singleDay ?? false,
        // CR-B2-07: normalize to UTC ISO so time is unambiguous across timezones
        startTime: toUtcIso(page1Data?.startDate ?? "", page1Data?.startTime ?? ""),
        endTime: toUtcIso(page1Data?.endDate ?? page1Data?.startDate ?? "", page1Data?.endTime ?? ""),
        // CR-B2-10: online events don't require a physical location.
        // `locationName` keeps carrying the URL for online events so that events
        // created before `meetingLink` existed in the schema still resolve — the
        // read side prefers meetingLink and falls back to locationName.
        locationName: isOnline ? (page1Data?.meetingLink ?? "") : (page1Data?.locationName ?? ""),
        // Now a real Data Gateway field (added 2026-08-03), so it persists.
        meetingLink: isOnline ? (page1Data?.meetingLink ?? "") : "",
        locationLat: isOnline ? 0 : (page1Data?.locationLat ?? 0),
        locationLng: isOnline ? 0 : (page1Data?.locationLng ?? 0),
        categoryIds: page1Data?.categoryIds ?? [],
        coverImage: coverImageUrl,
        visibility: page2Data?.visibility ?? "Public",
        registrationPolicy:
          page2Data?.registrationPolicy ?? eventRegistrationPolicy.NONE,
        capacity: page2Data?.capacity ?? null,
        groupIds: page2Data?.groupIds ?? [],
        attachmentUrls: [
          ...(page1Data?.existingAttachments ?? []),
          ...newAttachmentUrls,
        ].filter((url): url is string => !!url),
        Tags: [""],
        // CR-B2-10: eventType and meetingLink are schema-blocked — not included in mutation yet.
        // Add to INSERT_EVENT / UPDATE_EVENT variables once Data Gateway sync runs (open item 5).
      };

      if (isEditMode) {
        updateEvent({ input: finalData, itemId: eventToEdit!.ItemId });
      } else {
        insertEvent(finalData);
      }
    } catch {
      Toast.show({ type: "error", text1: t("ERROR_IMAGE_UPLOAD_FAILED") });
    }
  }, [
    page1Data,
    page2Data,
    isEditMode,
    eventToEdit,
    existingCoverRemoved,
    uploadImage,
    updateEvent,
    insertEvent,
    t,
  ]);

  const canGoBack = step > 1;

  return (
    <View className="flex-1 bg-obsidian">
      <ScrollView
        className="flex-1"
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ alignItems: "center" }}
      >
        <View className="bg-chalk p-4 md:p-6 rounded-[16px] w-full md:w-[85%] md:border md:border-ash-3">
          <View className="flex-row px-3 py-3 md:px-6 justify-between rounded-[16px] items-center mb-3 md:border md:border-ash-3">
            <TouchableOpacity
              className="flex-row items-center gap-2 hover:bg-ash-3 hover:rounded-lg"
              onPress={step === 1 ? handleCancel : goBack}
              activeOpacity={canGoBack ? 0.7 : 1}
            >
              <ArrowLeft
                color={
                  (canGoBack
                    ? themeVars["--color-onyx-default"]
                    : themeVars["--color-ash-1"]) as string
                }
              />
              <Text className="hidden sm:flex text-sm font-medium text-onyx">
                {t("BTN_BACK")}
              </Text>
            </TouchableOpacity>
            <ScStepper currentStep={step} steps={EVENT_STEPS} />
            {/* Show edit/create label on the right */}
            <Text className="hidden sm:flex text-sm font-medium text-onyx">
              {t(isEditMode ? "LABEL_EDIT_EVENT" : "LABEL_CREATE_EVENT")}
            </Text>
          </View>
          {step === 1 && (
            <Step1Details
              defaultValues={page1Data}
              existingCoverUrl={
                existingCoverRemoved ? null : eventToEdit?.coverImage
              }
              existingAttachments={eventToEdit?.attachmentUrls ?? []}
              onRemoveExistingCover={() => setExistingCoverRemoved(true)}
              onSubmit={handlePage1Submit}
              onCancel={handleCancel}
            />
          )}
          {step === 2 && (
            <Step2Settings
              defaultValues={page2Data}
              onSubmit={handlePage2Submit}
              onBack={goBack}
              onCancel={handleCancel}
            />
          )}
          {step === 3 && (
            <Step3Review
              page1Data={page1Data}
              page2Data={page2Data}
              categories={categories}
              onBack={goBack}
              onSubmit={handleFinalSubmit}
              onCancel={handleCancel}
              isSubmitting={isSubmitting}
              isEditMode={isEditMode}
            />
          )}
          <ConfirmModal
            visible={cancelModalVisible}
            title="LABEL_CANCEL_EVENT_CREATION"
            description="HINT_CANCEL_EVENT_CREATION"
            confirmText={t("BTN_YES_CANCEL")}
            cancelText={t("BTN_KEEP_EDITING")}
            onConfirm={handleCancelConfirm}
            onCancel={() => setCancelModalVisible(false)}
          />
        </View>
      </ScrollView>
    </View>
  );
};

export default EventsCreationScreen;
